import moment from 'moment';
import Store from '@/vuex/store';
import { time_ranges } from '@liveflight/resources/filters';
import {
  parse_date,
  get_availability,
  log,
  get_object_field,
} from '@/utilities/commons';
import { get_markup } from '@liveflight/utilities/price';
import {
  list_active_providers,
  get_known_carriers,
  has_capability,
} from '@liveflight/providers';

const get_filter_date_ranges = (filter) => {
  switch (filter) {
    case 'morning':
      return {
        from: 6,
        to: 12,
      };
    case 'early_afternoon':
      return {
        from: 12,
        to: 15,
      };
    case 'late_afternoon':
      return {
        from: 15,
        to: 18,
      };
    case 'evening':
      return {
        from: 18,
        to: 6,
      };
    default:
      break;
  }

  return {
    from: 0,
    to: 0,
  };
};

/**
 * Filter an offer list by the hour-range of one of the four journey
 * endpoints exposed on the normalized FlightOffer (firstDeparture |
 * firstArrival | lastDeparture | lastArrival). Offers missing the field
 * or with an unparseable timestamp fall through unchanged so a broken
 * payload from one provider never empties the page.
 * @param {Array<object>} list
 * @param {{filter: string, endpoint_field: string}} params
 * @returns {Array<object>}
 */
const filter_by_time_range = (list, params) => {
  const { filter, endpoint_field } = params;
  if (!list || !filter || !endpoint_field) {
    return list;
  }

  const { from, to } = get_filter_date_ranges(filter);

  return list.filter((offer) => {
    const date_string = get_object_field(offer, `${endpoint_field}.at`);
    if (!date_string) {
      log(`filter_by_time_range: missing ${endpoint_field}.at on offer`, offer);
      return true;
    }
    const date_object = parse_date(date_string);
    if (!date_object || !date_object.hour) {
      log(
        `filter_by_time_range: unparseable ${endpoint_field}.at "${date_string}" on offer`,
        offer
      );
      return true;
    }
    const date_hour = parseInt(date_object.hour.split(':')[0], 10);

    // caso SENZA mezzanotte nel range
    if (from <= to) {
      return date_hour >= from && date_hour < to;
    }
    // caso CON mezzanotte nel range
    return (
      (date_hour >= from && date_hour <= 23) ||
      (date_hour >= 0 && date_hour < to)
    );
  });
};

/**
 * Filter definitions for liveflight.
 * Each key matches a liveflight store state property.
 * Properties:
 *   - label: display text for the filter tag
 *   - default_value: value used to reset the filter
 *   - is_active(state): returns true when the filter is actively narrowing results
 *
 * Separated into result_filters (client-side, no new search) and
 * search_trigger_filters (removing them emits change-search-by and triggers a new search).
 */

const result_filters = {
  filter_availability: {
    label: 'In base al credito',
    default_value: false,
    is_active: (state) => state.filter_availability,
  },
  filter_carriers: {
    label: (state) => {
      // Merge known carriers from every active provider so the tag label
      // resolves regardless of which provider the selection came from.
      const carriers = list_active_providers().reduce(
        (acc, id) => ({ ...acc, ...get_known_carriers(id) }),
        {}
      );
      const names = state.filter_carriers.map((code) => carriers[code] || code);
      return names.join(', ');
    },
    default_value: [],
    is_active: (state) => state.filter_carriers.length > 0,
  },
  filter_providers: {
    label: (state) => `Provider: ${state.filter_providers.join(', ')}`,
    default_value: [],
    is_active: (state) => state.filter_providers.length > 0,
  },
  filter_round_trip_departure: {
    label: (state) =>
      `Andata: ${((time_ranges[state.filter_round_trip_departure] || {}).title || state.filter_round_trip_departure).toLowerCase()}`,
    default_value: '',
    is_active: (state) => !!state.filter_round_trip_departure,
  },
  filter_round_trip_arrival: {
    label: (state) =>
      `Arrivo: ${((time_ranges[state.filter_round_trip_arrival] || {}).title || state.filter_round_trip_arrival).toLowerCase()}`,
    default_value: '',
    is_active: (state) => !!state.filter_round_trip_arrival,
  },
  filter_return_trip_departure: {
    label: (state) =>
      `Ritorno: ${((time_ranges[state.filter_return_trip_departure] || {}).title || state.filter_return_trip_departure).toLowerCase()}`,
    default_value: '',
    is_active: (state) => !!state.filter_return_trip_departure,
  },
  filter_price_range: {
    label: (state) => {
      const { min: settings_min, max: settings_max } =
        state.filter_price_settings;
      const { min: range_min = settings_min, max: range_max = settings_max } =
        state.filter_price_range;
      return `Prezzo: ${Math.floor(range_min)}€ – ${Math.ceil(range_max)}€`;
    },
    default_value: {},
    is_active: (state) => {
      if (!state) {
        return false;
      }
      const { min: settings_min, max: settings_max } =
        state.filter_price_settings;
      if (!settings_min || !settings_max) {
        return false;
      }
      const { min: range_min = settings_min, max: range_max = settings_max } =
        state.filter_price_range;
      return range_min !== settings_min || range_max !== settings_max;
    },
  },
  filter_included_bags: {
    label: 'Bagagli inclusi',
    default_value: false,
    is_active: (state) => state.filter_included_bags,
  },
  filter_extra_bags: {
    label: 'Bagagli extra',
    default_value: false,
    is_active: (state) => state.filter_extra_bags,
  },
};

const cabin_labels = {
  ECONOMY: 'Economy',
  PREMIUM_ECONOMY: 'Premium economy',
  BUSINESS: 'Business',
  FIRST: 'First',
};

const search_trigger_filters = {
  max_number_of_connections: {
    label: 'Solo voli diretti',
    // Stored as a string upstream (search-by-connections.vue commits "0"/"2"),
    // so default_value matches that representation — equality with the
    // committed value drives `change-search-by` re-emission.
    default_value: '2',
    is_active: (state) => String(state.max_number_of_connections) === '0',
    client_side_validator: (state, offer) => {
      const max_number_of_connections = Number(state.max_number_of_connections);
      return offer.itineraries.every(
        (itinerary) => itinerary.stopsCount <= max_number_of_connections
      );
    },
  },
  included_checked_bags_only: {
    label: 'Tariffe con bagagli in stiva',
    default_value: '0',
    is_active: (state) =>
      state.included_checked_bags_only === '1' ||
      state.included_checked_bags_only === 1,
  },
  cabin: {
    label: (state) => `Classe: ${cabin_labels[state.cabin] || state.cabin}`,
    default_value: '',
    is_active: (state) => !!state.cabin,
  },
};

/** All filter definitions combined. */
const filter_definitions = { ...result_filters, ...search_trigger_filters };

/** Default values for all filters, derived from filter_definitions. */
export const default_filters = Object.fromEntries(
  Object.entries(filter_definitions).map(([key, { default_value }]) => [
    key,
    default_value,
  ])
);

/**
 * Resets a single filter to its default value.
 * For search_trigger_filters, also emits change-search-by to trigger a new search.
 * @param {string} key - The filter key matching a liveflight store state property (e.g. "filter_carriers")
 */
export const remove_filter = (key) => {
  const definition = filter_definitions[key];
  if (!definition) {
    return;
  }

  const previous_value = Store.state.liveflight[key];
  Store.commit(`liveflight/${key}`, definition.default_value);

  if (key in search_trigger_filters) {
    if (previous_value === definition.default_value) {
      return;
    }
    window.eventBus.$emit('change-search-by', {
      [key]: definition.default_value,
    });
  }
};

/**
 * Returns the list of currently active filters with their key and label.
 * @returns {Array<{key: string, label: string}>}
 */
export const get_active_filters = () => {
  const state = Store.state.liveflight;
  return Object.entries(filter_definitions)
    .filter(([, definition]) => definition.is_active(state))
    .map(([key, definition]) => ({
      key,
      label:
        typeof definition.label === 'function'
          ? definition.label(state)
          : definition.label,
    }));
};

export function filter_flight_offers() {
  let filtered = Store.state.liveflight.results.flights;
  let filtered_all_carriers = Store.state.liveflight.results.flights;

  // Safeguard we might not have results to filter
  if (!filtered) {
    return {
      filtered: [],
      filtered_all_carriers: [],
    };
  }

  // Drop offers from providers that don't honour the active
  // search-trigger filters at the API level — otherwise their results
  // would contain offers violating the user's selected constraints
  // (e.g. non-direct flights when "Solo voli diretti" is on).
  const state = Store.state.liveflight;

  filtered = Object.values(search_trigger_filters)
    .filter((definition) => definition.is_active(state))
    .reduce(
      (all, search_trigger_filter) =>
        all.filter((f) => {
          if (!f || !f._provider) {
            return false;
          }
          if (has_capability(f._provider, 'searchTriggerFilters')) {
            return true;
          }
          if (search_trigger_filter.client_side_validator) {
            return search_trigger_filter.client_side_validator(state, f);
          }
          return false;
        }),
      filtered
    );

  const {
    filter_carriers,
    filter_round_trip_departure,
    filter_round_trip_arrival,
    filter_return_trip_departure,
    filter_included_bags,
    filter_extra_bags,
    filter_providers = [],
  } = state;

  // Per un one-way lastDeparture coincide con firstDeparture, quindi
  // il filtro return-trip non rimuove offerte one-way a meno che
  // l'utente non selezioni una fascia oraria.
  filtered = [
    { filter: filter_round_trip_departure, endpoint_field: 'firstDeparture' },
    { filter: filter_round_trip_arrival, endpoint_field: 'firstArrival' },
    { filter: filter_return_trip_departure, endpoint_field: 'lastDeparture' },
  ].reduce((result, config) => filter_by_time_range(result, config), filtered);

  if (filter_included_bags) {
    filtered = filtered.filter((f) => {
      if (f.travelerPricings[0].fareDetailsBySegment[0].includedCheckedBags) {
        const icb =
          f.travelerPricings[0].fareDetailsBySegment[0].includedCheckedBags;
        if (icb.quantity && icb.quantity > 0) return true;
        if (icb.weight && icb.weight > 0) return true;
      }
      return false;
    });
  }
  if (filter_extra_bags) {
    filtered = filtered.filter((f) => {
      if (!f) {
        return false;
      }
      const { additionalServices = [] } = f.price || {};
      return additionalServices.some(
        (as) => String(as.type).toLowerCase() === 'checked_bags'
      );
    });
  }

  // filtro in base al credito disponibile
  if (Store.state.liveflight.filter_availability) {
    const availability = get_availability();
    filtered = filtered.filter(
      (f) => availability.total >= Number(get_markup(f).mu_price)
    );
  }

  // filtro in base al range di prezzo
  const price_range = Store.state.liveflight.filter_price_range;
  const price_settings = Store.state.liveflight.filter_price_settings;
  if (
    price_range.min !== undefined &&
    price_settings.min !== undefined &&
    (price_range.min !== price_settings.min ||
      price_range.max !== price_settings.max)
  ) {
    filtered = filtered.filter((f) => {
      const price = Number(get_markup(f).mu_price);
      return price >= price_range.min && price <= price_range.max;
    });
  }

  // mi salvo i risultati filtrati PRIMA dell'applicazione del filtro per carriers, perché ho bisogno dei totali nella sidebar dei filtri
  filtered_all_carriers = filtered;

  // filter by carrier code => visualizzo le flightoffer che abbiano almeno un carrier code di quelli desiderati tra i segmenti del primo itinerario
  if (filter_carriers.length) {
    filtered = filtered.filter((f) => {
      const {
        itineraries: [{ segments: [first_segment = {}] = [] } = {}] = [],
      } = f || {};
      const { carrier: { code = '' } = {} } = first_segment;
      return filter_carriers.includes(code);
    });
  }

  // Dev-only UI — in prod filter_providers stays empty so this is a no-op
  if (filter_providers.length) {
    filtered = filtered.filter(
      (f) => !!f && filter_providers.includes(f._provider)
    );
  }

  return {
    filtered,
    filtered_all_carriers,
  };
}

/**
 * Formats a numeric time (hour) into a time string using moment.js
 * @param {number} time - The hour as a number (0-23)
 * @param {string} format - The format to use (default: 'HH:mm')
 * @returns {string} The formatted time string
 */
export const time_format = (time, format = 'HH:mm') => {
  if (typeof time !== 'number' || time < 0 || time > 23) {
    return '';
  }

  const hour = Math.floor(time);
  const minutes = Math.round((time - hour) * 60);

  return moment().hour(hour).minute(minutes).format(format);
};

export const set_direct_flight_filter_active = (selected) => {
  const max_number_of_connections = selected ? '0' : '2';
  Store.commit(
    'liveflight/max_number_of_connections',
    max_number_of_connections
  );

  window.eventBus.$emit('change-search-by', {
    max_number_of_connections,
  });
};

/**
 * Resets all result filters to their default values.
 * Also resets the direct flight search-by filter via set_direct_flight_filter_active.
 */
export const remove_all_filters = () => {
  Object.keys(default_filters).forEach(remove_filter);
};
